"use client"

import { useMemo, useState, type ReactNode } from "react"
import { AlertTriangle, CheckCircle } from "lucide-react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readNonNegative } from '@/lib/tools/numeric-input'

// Per-feature savings estimates preserved verbatim from legacy logic.
// These are conservative tier-based ranges, not vendor-specific claims:
//   Energy monitoring: $30/yr (5–10% of typical $300/yr operating cost)
//   Vacation mode: $25/yr (1–2 trips reduced energy + spoiled-food avoidance)
//   Door-open alerts: $40/yr (door-left-open avoidance + spoilage)
//   Inventory tracking: $100/yr (USDA: average household wastes $100s/yr)
//   Base WiFi/app: $20/yr (remote diagnostic value)
// Verdict tone is keyed by payback period:
//   < 3 yr  → healthy ("worth it")
//   3–5 yr  → healthy ("good ROI")
//   5–8 yr  → watch ("borderline")
//   ≥ 8 yr  → replace tone ("skip — not worth the premium")

type Tone = "healthy" | "watch" | "replace"

interface FeatureRow {
  feature: string
  annualSavings: number
  benefits: string[]
}

interface Verdict {
  tone: Tone
  headline: string
  subline: string
  totalPremium: number
  annualSavings: number
  paybackYears: number
  tenYearNet: number
  features: FeatureRow[]
}

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Verdict · Worth it",
  watch: "Verdict · Borderline",
  replace: "Verdict · Skip the smart premium",
}
const TONE_CTA: Record<Tone, { label: string; href: string }> = {
  healthy: { label: "Browse smart refrigerators →", href: "/collections/counter-depth-refrigerators" },
  watch: { label: "Browse French door fridges →", href: "/collections/french-door-refrigerators" },
  replace: { label: "Browse non-smart French door →", href: "/collections/french-door-refrigerators" },
}

function compute(
  premium: number,
  hasEnergyMonitoring: boolean,
  hasVacationMode: boolean,
  hasDoorAlerts: boolean,
  hasInventoryTracking: boolean,
): Verdict {
  const features: FeatureRow[] = []
  let annualSavings = 0

  if (hasEnergyMonitoring) {
    annualSavings += 30
    features.push({
      feature: "Energy monitoring",
      annualSavings: 30,
      benefits: [
        "Real-time kWh tracking",
        "Surface inefficient cycling patterns",
        "Optimize temperature setpoints",
      ],
    })
  }
  if (hasVacationMode) {
    annualSavings += 25
    features.push({
      feature: "Vacation mode",
      annualSavings: 25,
      benefits: ["Auto-adjusts temps when away", "Reduces energy on extended trips"],
    })
  }
  if (hasDoorAlerts) {
    annualSavings += 40
    features.push({
      feature: "Door-open alerts",
      annualSavings: 40,
      benefits: ["Alerts when door left open", "Prevents spoilage + energy waste"],
    })
  }
  if (hasInventoryTracking) {
    annualSavings += 100
    features.push({
      feature: "Inventory tracking",
      annualSavings: 100,
      benefits: [
        "Track expiration dates",
        "Reduce food waste — USDA avg ~$100s/household/yr",
        "Smarter shopping lists",
      ],
    })
  }
  // Base WiFi / app
  annualSavings += 20
  features.push({
    feature: "WiFi + app control (base)",
    annualSavings: 20,
    benefits: ["Remote temperature control", "Diagnostic alerts to phone"],
  })

  const paybackYears = annualSavings > 0 ? premium / annualSavings : Infinity
  const tenYearNet = annualSavings * 10 - premium

  let tone: Tone
  let headline: string
  let subline: string
  if (paybackYears < 3) {
    tone = "healthy"
    headline = "Worth it."
    subline = `Smart features pay back in ${paybackYears.toFixed(1)} years — net 10-year value of $${tenYearNet.toLocaleString("en-US", { maximumFractionDigits: 0 })}. Strong ROI.`
  } else if (paybackYears < 5) {
    tone = "healthy"
    headline = "Worth it."
    subline = `Pays back in ${paybackYears.toFixed(1)} years — net 10-year value of $${tenYearNet.toLocaleString("en-US", { maximumFractionDigits: 0 })}. Good ROI plus convenience.`
  } else if (paybackYears < 8) {
    tone = "watch"
    headline = "Borderline."
    subline = `Pays back in ${paybackYears.toFixed(1)} years. The math is soft — only worth it if you'll actually use the app and value the convenience.`
  } else {
    tone = "replace"
    headline = "Skip the smart premium."
    subline =
      paybackYears === Infinity
        ? "No quantified savings. Skip the smart premium and put the difference toward better insulation or a higher-tier ENERGY STAR unit."
        : `Pays back in ${paybackYears.toFixed(1)} years — beyond typical ownership. Convenience-only ROI.`
  }

  return {
    tone,
    headline,
    subline,
    totalPremium: premium,
    annualSavings,
    paybackYears,
    tenYearNet,
    features,
  }
}

const RELATED = [
  {
    title: "Refrigerator Cost of Ownership Calculator",
    category: "Energy & Cost",
    description: "True 10-year purchase + energy + maintenance cost.",
    href: "/tools/refrigerator-cost-of-ownership-calculator",
    time: "~45 sec",
  },
  {
    title: "Energy Star Savings Calculator",
    category: "Energy & Cost",
    description: "Annual + 10-year savings vs an ENERGY STAR model.",
    href: "/tools/energy-star-savings-calculator",
    time: "~30 sec",
  },
  {
    title: "Find Your Fridge",
    category: "Specialty",
    description: "Explore product groups based on your answers.",
    href: "/tools/find-your-fridge",
    time: "~2 min",
  },
  {
    title: "Refrigerator Size Calculator",
    category: "Size & Capacity",
    description: "Confirm size before fixating on features.",
    href: "/tools/refrigerator-size-calculator",
    time: "~30 sec",
  },
]

function InputLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
        {children}
      </label>
      {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
    </div>
  )
}

function FeatureToggle({
  checked,
  onChange,
  label,
  saves,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  saves: string
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={[
        "px-3 py-3 text-left transition-all w-full rounded-lg border",
        checked
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-900 border-gray-200 hover:border-[#ff9951]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-bold text-[13px] leading-tight">{label}</div>
        <div
          className={`text-[11px] font-mono font-bold ${checked ? "text-white/70" : "text-[#ff9951]"}`}
        >
          {saves}
        </div>
      </div>
    </button>
  )
}

export function SmartFeaturesRoiCalculatorPageClient() {
  const [premium, setPremium] = useState("500")
  const [energyMon, setEnergyMon] = useState(false)
  const [vacationMode, setVacationMode] = useState(false)
  const [doorAlerts, setDoorAlerts] = useState(false)
  const [inventory, setInventory] = useState(false)

  const verdict = useMemo(() => {
    const p = readNonNegative(premium, { max: 5000 })
    if (p === null) return null
    return compute(p, energyMon, vacationMode, doorAlerts, inventory)
  }, [premium, energyMon, vacationMode, doorAlerts, inventory])

  return (
    <ToolPageShell
      toolName="Smart Refrigerator Worth It Calculator"
      heroEyebrow="Specialty"
      heroTitlePre="Are smart features"
      heroTitleItalic="actually"
      heroTitlePost="worth the premium?"
      heroSubtitle="Tell us what the smart upgrade actually costs and which features it includes. We compute payback in years and give you a tone-keyed verdict — worth it, borderline, or skip."
      heroMeta={["⏱ ~30 sec", "Conservative per-feature ROI tiers"]}
      heroShare={<ToolShareBar title="Are smart fridge features worth it?" />}
      faqs={[
        {
          question: "Where do the per-feature savings come from?",
          answer:
            "Conservative tier-based estimates: energy monitoring saves about $30/yr (5–10% of a typical $300/yr operating cost), door-open alerts $40/yr (avoided spoilage + energy), and inventory tracking ~$100/yr (USDA reports the average US household wastes hundreds of dollars in food per year). They're not vendor claims.",
        },
        {
          question: "What about features I can't quantify?",
          answer:
            "Voice control, internal cameras, recipe suggestions, smart-home integration — all real conveniences, but with no defensible dollar value. They factor into the verdict copy, not the math.",
        },
        {
          question: "Why does the verdict say skip on long paybacks?",
          answer:
            "If smart features take more than 8 years to pay back, you're financing convenience — not savings. Refrigerator service life is roughly 10–15 years (DOE / AHAM), so most of the payback period would be the entire useful life of the unit.",
        },
        {
          question: "Does this account for app discontinuation risk?",
          answer:
            "No, but you should. Manufacturer apps come and go. If the maker drops support in year 5, those features become a one-star Yelp review. Stick with brands that have a track record of long app support.",
        },
        {
          question: "What's the right premium to pay?",
          answer:
            "If only the base WiFi tier is included, the savings ($20/yr) justify roughly $100–200 in premium — anything beyond that needs concrete features (door alerts, inventory) to make the math work.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "If smart's worth it — see the lineup.",
        body: "Browse counter-depth refrigerators with the strongest smart-feature integration and live retailer pricing.",
        buttonLabel: "Browse counter-depth refrigerators",
        buttonHref: "/collections/counter-depth-refrigerators",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              The smart-feature math
            </div>
            <div className="space-y-6">
              <div>
                <InputLabel hint="Smart model price minus comparable non-smart">Premium ($)</InputLabel>
                <input
                  type="number"
                  step="50"
                  value={premium}
                  onChange={(e) => setPremium(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>

              <div>
                <InputLabel>Features included (toggle yours)</InputLabel>
                <div className="space-y-2">
                  <FeatureToggle
                    checked={energyMon}
                    onChange={setEnergyMon}
                    label="Energy monitoring"
                    saves="+$30/yr"
                  />
                  <FeatureToggle
                    checked={vacationMode}
                    onChange={setVacationMode}
                    label="Vacation mode"
                    saves="+$25/yr"
                  />
                  <FeatureToggle
                    checked={doorAlerts}
                    onChange={setDoorAlerts}
                    label="Door-open alerts"
                    saves="+$40/yr"
                  />
                  <FeatureToggle
                    checked={inventory}
                    onChange={setInventory}
                    label="Inventory tracking"
                    saves="+$100/yr"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-2 leading-[1.5]">
                  Base WiFi/app control adds $20/yr on its own. Voice control, cameras, and recipe suggestions are convenience-only.
                </p>
              </div>
            </div>
          </div>

          {/* Verdict */}
          <div>
            {verdict && (
              <>
                <div
                  className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
                  style={{
                    background: TONE_BG[verdict.tone],
                    border: `1px solid ${TONE_BORDER[verdict.tone]}30`,
                  }}
                >
                  <div
                    className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                    style={{ color: TONE_BORDER[verdict.tone] }}
                  >
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: TONE_BORDER[verdict.tone] }}
                    />
                    {TONE_LABEL[verdict.tone]}
                  </div>
                  <div
                    className="text-gray-900 font-black"
                    style={{
                      fontSize: "clamp(40px, 6.5vw, 76px)",
                      letterSpacing: "-0.05em",
                      lineHeight: 0.95,
                      textWrap: "balance" as never,
                    }}
                  >
                    {verdict.headline}
                  </div>
                  <p
                    className="mt-5 md:mt-6 text-gray-700"
                    style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
                  >
                    {verdict.subline}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 border-y border-gray-300 sm:divide-x divide-gray-300 mb-6 md:mb-8">
                  <div className="py-5 sm:pr-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Premium
                    </div>
                    <div className="font-black text-gray-900" style={{ fontSize: 26, letterSpacing: "-0.03em" }}>
                      {`$${verdict.totalPremium.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 sm:px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Annual savings
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 26,
                        letterSpacing: "-0.03em",
                        color: TONE_BORDER[verdict.tone],
                      }}
                    >
                      {`$${verdict.annualSavings.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 sm:px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Payback
                    </div>
                    <div className="font-black text-gray-900" style={{ fontSize: 26, letterSpacing: "-0.03em" }}>
                      {Number.isFinite(verdict.paybackYears) ? `${verdict.paybackYears.toFixed(1)} yr` : "—"}
                    </div>
                  </div>
                  <div className="py-5 sm:pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      10-yr net
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 26,
                        letterSpacing: "-0.03em",
                        color: verdict.tenYearNet >= 0 ? "#10a65c" : "#dc2626",
                      }}
                    >
                      {`$${verdict.tenYearNet.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                </div>

                {verdict.features.length > 0 && (
                  <div className="mb-8">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                      Per-feature breakdown
                    </div>
                    <ul className="space-y-2">
                      {verdict.features.map((f) => (
                        <li
                          key={f.feature}
                          className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200"
                        >
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                            style={{ background: TONE_BORDER[verdict.tone] }}
                          >
                            {verdict.tone === "replace" ? (
                              <AlertTriangle className="w-4 h-4" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-extrabold text-[14px]">{f.feature}</div>
                              <div className="text-[12px] font-mono font-bold text-[#ff9951]">
                                +{`$${f.annualSavings}`}/yr
                              </div>
                            </div>
                            <p className="text-[12px] text-gray-600 leading-[1.5] mt-0.5">
                              {f.benefits.join(" · ")}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <a
                  href={TONE_CTA[verdict.tone].href}
                  className="block h-14 rounded-lg text-[15px] font-extrabold tracking-tight text-white inline-flex items-center justify-center w-full"
                  style={{
                    background: verdict.tone === "replace" ? "#ff9951" : "#111827",
                    boxShadow:
                      verdict.tone === "replace" ? "0 8px 22px rgba(255,153,81,0.35)" : undefined,
                  }}
                >
                  {TONE_CTA[verdict.tone].label}
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If smart's worth it"
          title="Counter-depth refrigerators with the best smart integration"
          description="Built-in look, full-size capacity, modern WiFi + app ecosystems."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "Conservative per-feature savings",
                d: "Energy monitoring $30/yr (5–10% of typical $300/yr operating cost). Door-open alerts $40/yr (avoided spoilage + energy). Inventory tracking $100/yr (USDA reports US households waste hundreds of dollars in food per year).",
              },
              {
                n: "02",
                t: "Payback = premium ÷ annual savings",
                d: "Standard ROI. We tone-key the verdict at three thresholds: < 5 yr healthy (worth it), 5–8 yr watch (borderline), ≥ 8 yr replace (skip the premium).",
              },
              {
                n: "03",
                t: "10-year net value",
                d: "Annual savings × 10 − premium. Refrigerator service life is roughly 10–15 years (DOE / AHAM), so 10 years is a fair planning horizon.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">{m.t}</h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door refrigerators (smart + non-smart)"
          description="Compare smart and non-smart French door models head to head."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
